import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCar, faCalendarAlt, faChevronDown, faPlus } from '@fortawesome/free-solid-svg-icons';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import { primaryColor } from '../../../../../utils/comon';
import apiServices from '../../../../../services/apiService';
import storageService from '../../../../../utils/storageService';

const OfferLease = () => {
    const navigate = useNavigate();
    const { user: authUser } = useSelector((state) => state.auth);
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        startDate: '',
        endDate: ''
    });

    useEffect(() => {
        const fetchSlots = async () => {
            const currentUnitId = authUser?.unitId || authUser?.branchorunitID || Number(storageService.getItem("unitId")) || Number(storageService.getItem("branchorunitID")) || 0;
            if (!currentUnitId) return;

            try {
                const res = await apiServices({
                    method: 'GET',
                    url: `/ParkingSlotMaster/unit/${currentUnitId}`
                });
                if (res.success && res.data) {
                    const apiData = res.data.Data || [];
                    // Filter out already occupied slots if needed, or just show all
                    setSlots(apiData);
                }
            } catch (error) {
                console.error("Error fetching slots", error);
            }
        };
        fetchSlots();
    }, [authUser]);

    const handleInputChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async () => {
        if (!selectedSlot) {
            toast.error("Please select a parking slot.");
            return;
        }
        if (!formData.startDate || !formData.endDate) {
            toast.error("Please provide both Start Date and End Date.");
            return;
        }

        const ownerUnitId = authUser?.unitId || authUser?.branchorunitID || 0;

        const payload = {
            slotId: selectedSlot,
            ownerUnitId: ownerUnitId,
            startDate: new Date(formData.startDate).toISOString(),
            endDate: new Date(formData.endDate).toISOString()
        };

        setIsSubmitting(true);
        try {
            const res = await apiServices({
                method: "POST",
                url: "/RentalParking/offer",
                data: payload
            });

            if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                toast.success("Lease offer submitted successfully!");
                navigate('/zentres/parking-management/rental-parking');
            } else {
                toast.error(res?.message || res?.data?.Message || "Failed to submit lease offer.");
            }
        } catch (error) {
            console.error("Error submitting lease offer:", error);
            toast.error("An error occurred while submitting the offer.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-4 sm:px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto h-full relative scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Offer Commercial Lease to Site"
                subHeading="Submit a direct parking lease proposal to Building Management"
            />

            <div className="mt-4 flex flex-col gap-4 w-full">
                {/* Target Section */}
                <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-lg">
                            <FontAwesomeIcon icon={faBuilding} />
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-gray-800">Site / Building Management</h3>
                            <p className="text-[10px] text-gray-500 mt-0.5">Commercial lease proposal offered directly to Site Admin for centralized building allocation</p>
                        </div>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[9px] font-bold rounded-full uppercase tracking-wider">
                        Exclusive Target
                    </span>
                </div>

                {/* Slot Selection */}
                <div className="bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-gray-50 dark:bg-actionitembg px-4 py-2 border-b border-gray-200 dark:border-bordercolor flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2 text-red-500 font-bold text-xs">
                            <FontAwesomeIcon icon={faCar} />
                            SELECT ELIGIBLE PARKING SLOT
                        </div>
                        <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs" />
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        {slots.length === 0 ? (
                            <div className="col-span-3 text-sm text-gray-500 py-4 text-center">No parking slots found for your unit.</div>
                        ) : (
                            slots.map((slot) => {
                                const slotId = slot.Id || slot.id;
                                const isSelected = selectedSlot === slotId;
                                return (
                                    <div
                                        key={slotId}
                                        onClick={() => setSelectedSlot(slotId)}
                                        className={`border rounded-lg p-3 cursor-pointer transition-all ${isSelected
                                            ? "border-red-500 bg-red-50/30"
                                            : "border-gray-200 hover:border-gray-300"
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-red-500" : "border-gray-300"
                                                    }`}>
                                                    {isSelected && <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>}
                                                </div>
                                                <span className="font-bold text-xs text-gray-800 dark:text-textprimary">{slot.SlotNumber}</span>
                                            </div>
                                            <span className={`px-2 py-0.5 text-[9px] font-bold rounded ${slot.Status?.toLowerCase() === 'available' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                {slot.Status || 'Unknown'}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-500 pl-5">{slot.ParkingTypeName || 'Standard'}</p>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Lease Terms */}
                <div className="bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-gray-50 dark:bg-actionitembg px-4 py-2 border-b border-gray-200 dark:border-bordercolor flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2 text-red-500 font-bold text-xs">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            LEASE DURATION
                        </div>
                        <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs" />
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-bold text-gray-700 mb-1.5">Start Date <span className="text-red-500">*</span></label>
                            <input
                                type="datetime-local"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleInputChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-red-500 bg-gray-50/50 text-gray-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-gray-700 mb-1.5">End Date <span className="text-red-500">*</span></label>
                            <input
                                type="datetime-local"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleInputChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-red-500 bg-gray-50/50 text-gray-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-3 mt-2 pb-10">
                    <button
                        onClick={() => navigate('/zentres/parking-management/rental-parking')}
                        className="px-5 py-2 rounded-lg text-xs font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                        className="px-5 py-2 rounded-lg text-xs font-bold text-white transition-colors flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70"
                        style={{ backgroundColor: primaryColor() }}
                    >
                        <FontAwesomeIcon icon={faPlus} /> {isSubmitting ? "Submitting..." : "Submit Lease Offer"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OfferLease;
